import { Box, List, ListItem, Menu, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MouseEvent, useCallback, useRef } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { FilterFunnel02Icon } from '@/components/icons/FilterFunnel02Icon';
import { StyledCounter } from '@/components/notifications/StyledCounter/StyledCounter';
import { useDelayedCloseBehavior } from '@/hooks/useDelayedCloseBehavior';
import { COLORS } from '@/styles/tokens/colors';

import { AsyncFilterGroupDisplay } from './AsyncFilterGroupDisplay';
import { StaticFilterGroupDisplay } from './StaticFilterGroupDisplay';
import { FilterGroup, StaticFilterGroup } from './types';
import { countGroupSelected } from './utils';

const ListItemStyled = styled(ListItem)(() => ({
  borderRadius: 4,
  minHeight: 36,
  padding: '10px 12px',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '&:hover': {
    backgroundColor: COLORS.FUNCTIONAL.HOVER,
  },
}));

interface FilterGroupNavProps {
  /** Filter groups to display */
  groups: FilterGroup[];
  /** Currently selected filter values */
  value: Record<string, string[]>;
  /** Currently active group (hovered/selected) */
  activeGroupId: string | null;
  /** Called when a group is selected */
  onSelectGroup: (groupId: string | null) => void;
  /** Called when "Clear all" is clicked */
  onClearAll: () => void;
  /** Called when an option is toggled */
  onToggleOption: (groupId: string, optionValue: string) => void;
}

/**
 * First-level navigation component showing filter groups and a "Clear all" option
 */
export function FilterGroupNav({
  groups,
  value,
  activeGroupId,
  onSelectGroup,
  onClearAll,
  onToggleOption,
}: FilterGroupNavProps) {
  const filterButtonRef = useRef<HTMLDivElement | null>(null);

  const { isOpen, setIsMouseInTargetArea } = useDelayedCloseBehavior({
    onOpenChange: (isOpen) => {
      if (!isOpen) {
        onSelectGroup(null);
      }
    },
  });

  const setFilterButtonRef = useCallback((ref: HTMLDivElement | null) => {
    filterButtonRef.current = ref;
  }, []);

  const handleMouseEnter = useCallback(
    (groupId: string) => (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onSelectGroup(groupId);
      setFilterButtonRef(event.currentTarget as HTMLDivElement);
      setIsMouseInTargetArea(true);
    },
    [onSelectGroup, setFilterButtonRef, setIsMouseInTargetArea]
  );

  const handleClearAll = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onClearAll();
  };

  const hasSelections = Object.values(value).some(
    (groupSelections) => groupSelections.length > 0
  );

  const activeGroup = activeGroupId
    ? groups.find((g) => g.id === activeGroupId) || null
    : null;

  const handleSelectAndClose = useCallback(
    (groupId: string, optionValue: string) => {
      onToggleOption(groupId, optionValue);
      const groupConfig = groups.find((g) => g.id === groupId);
      if (groupConfig && groupConfig.multi === false) {
        onSelectGroup(null);
        setIsMouseInTargetArea(false);
      }
    },
    [onToggleOption, onSelectGroup, groups, setIsMouseInTargetArea]
  );

  const handleMouseLeave = useCallback(() => {
    setIsMouseInTargetArea(false);
  }, [setIsMouseInTargetArea]);

  return (
    <Box width={220}>
      <List sx={{ padding: `0 !important` }}>
        <Box
          onMouseEnter={() => setIsMouseInTargetArea(true)}
          onMouseLeave={handleMouseLeave}
        >
          {groups.map((group) => {
            const selected = countGroupSelected(group.id, value);
            const isActive = activeGroupId === group.id;
            const Icon = group.icon || <FilterFunnel02Icon size={16} />;

            return (
              <ListItemStyled
                key={group.id}
                onMouseEnter={handleMouseEnter(group.id)}
                sx={{
                  bgcolor: isActive ? COLORS.GRAY[100] : 'transparent',
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ color: COLORS.TEXT.PRIMARY }}
                >
                  <StyledCounter
                    color="error"
                    show={!!selected}
                    badgeContent={selected}
                  >
                    <Box
                      display="flex"
                      height={16}
                      width={16}
                      sx={{ '& svg': { height: 16, width: 16 } }}
                    >
                      {Icon}
                    </Box>
                  </StyledCounter>
                  <Typography color={COLORS.TEXT.PRIMARY} variant="body-xs">
                    {group.label}
                  </Typography>
                </Stack>
                <ChevronRightIcon size={16} color={COLORS.TEXT.PRIMARY} />
              </ListItemStyled>
            );
          })}
        </Box>

        {hasSelections && (
          <>
            <SelectItemGroupLabel />
            <ListItem sx={{ justifyContent: 'flex-end', py: 0.5, px: 1 }}>
              <Button variant="transparent" size="xs" onClick={handleClearAll}>
                Clear all
              </Button>
            </ListItem>
          </>
        )}
      </List>

      {activeGroup && filterButtonRef.current && isOpen && (
        <Menu
          open={true}
          anchorEl={filterButtonRef.current}
          onClose={() => {
            onSelectGroup(null);
            setIsMouseInTargetArea(false);
          }}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          MenuListProps={{
            sx: { padding: 0 },
            onMouseEnter: () => setIsMouseInTargetArea(true),
            onMouseLeave: () => setIsMouseInTargetArea(false),
          }}
          sx={{
            maxHeight: 700,
            pointerEvents: 'none',
            '& .MuiPaper-root': {
              pointerEvents: 'auto',
              marginLeft: 1,
              // The child anchors to the active row. Offset the parent's
              // 4px list inset and 1px border so both papers share a top edge.
              marginTop: '-5px',
              overflow: 'hidden',
            },
            '& .MuiMenu-list': {
              padding: 0,
            },
          }}
        >
          {'async' in activeGroup && activeGroup.async ? (
            <AsyncFilterGroupDisplay
              group={activeGroup}
              value={value[activeGroup.id] || []}
              onSelectAndClose={(optionValue) =>
                handleSelectAndClose(activeGroup.id, optionValue)
              }
            />
          ) : (
            <StaticFilterGroupDisplay
              group={activeGroup as StaticFilterGroup}
              value={value[activeGroup.id] || []}
              onSelectAndClose={(optionValue) =>
                handleSelectAndClose(activeGroup.id, optionValue)
              }
            />
          )}
        </Menu>
      )}
    </Box>
  );
}
